<script>
import knobDrag from '../js/knobDrag.js';
import NoteGlyph from './NoteGlyph.vue';

export default {
  components: { NoteGlyph },

  mixins: [knobDrag],

  // Live incoming-CC value (MDL-269), provided by the CcBadge wrapper when
  // a mapped CC is arriving. Display-only: renders a transient ghost marker
  // on the ring at the value the device is applying. Never written back.
  inject: {
    ccLiveValue: { default: null }
  },

  props: {
    label: {
      type: String,
      required: true
    },
    tip: {
      type: String,
      required: false,
      default: ''
    },
    value: {
      type: [Number, String],
      required: false,
      default: 0
    },
    max: {
      type: Number,
      required: false,
      default: 127
    },
    min: {
      type: Number,
      required: false,
      default: 0
    },
    snaps: {
      type: Array,
      required: false,
      default: () => []
    },
    // Bipolar only: the knob runs -range … +range (default -100 … +100).
    range: {
      type: Number,
      required: false,
      default: 100
    },
    // Appended to the readout (Phase knob: '°').
    unit: {
      type: String,
      required: false,
      default: ''
    },
    // 'unipolar' | 'bipolar' | 'enum'
    mode: {
      type: String,
      required: false,
      default: 'unipolar',
      validator: v => ['unipolar', 'bipolar', 'enum'].includes(v)
    },
    // Required when mode === 'enum': Array<{ value: string|number, label: string }>
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    // 'circular' — circular enum layout, no ring fill, wrap-around drag
    variant: {
      type: String,
      required: false,
      default: null,
      validator: v => v === null || v === 'circular'
    },
    // When true (and mode is unipolar), value is distributed logarithmically.
    // min and max must both be > 0.
    logarithmic: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  emits: ['update:value'],

  data() {
    return { tipVisible: false, _tipTimer: null };
  },

  computed: {
    // ------------------------------------------------------------------ //
    // Shared helpers
    // ------------------------------------------------------------------ //
    isBipolar()   { return this.mode === 'bipolar'; },
    isEnum()      { return this.mode === 'enum'; },
    isCircular()  { return this.variant === 'circular'; },

    // ------------------------------------------------------------------ //
    // UNIPOLAR
    // ------------------------------------------------------------------ //
    pct() {
      if (this.logarithmic) {
        const v   = Math.max(this.min, this.value ?? this.min);
        return (Math.log(v) - Math.log(this.min)) / (Math.log(this.max) - Math.log(this.min));
      }
      return ((this.value ?? this.min) - this.min) / (this.max - this.min);
    },

    fillDeg() {
      return this.pct * 360;
    },

    snapVars() {
      const vars = { '--knob-snap-count': this.snaps.length };
      this.snaps.forEach((snap, i) => {
        vars[`--knob-snap-${i}`] = ((snap - this.min) / (this.max - this.min)) * 360 + 'deg';
      });
      return vars;
    },

    // ------------------------------------------------------------------ //
    // BIPOLAR  (-100 … 0 … +100)
    // Arc spans ±120° centred at 12 o'clock (0° in CSS conic = top).
    // Fill is centred on 12 o'clock:
    //   value=0  → no fill  (from=360°, to=360°)
    //   value>0  → fill clockwise from 0° to +(value/100)*120°
    //   value<0  → fill counter-clockwise: from 360°-(abs/100)*120° to 360°
    // ------------------------------------------------------------------ //
    bipolarPct() {
      // 0.0 = -range, 0.5 = 0, 1.0 = +range  (used for cap rotation only)
      return ((this.value ?? 0) + this.range) / (2 * this.range);
    },

    // Rotation of the cap indicator: maps -range→-120°, 0→0°, +range→+120°
    bipolarCapDeg() {
      return ((this.value ?? 0) / this.range) * 120;
    },

    // Ring angle of each non-zero snap (zero has its own centre marker).
    bipolarSnapTicks() {
      return this.snaps
        .filter(snap => snap !== 0)
        .map(snap => ({ value: snap, angle: (snap / this.range) * 120 }));
    },

    // conic-gradient fill arc expressed as absolute from/to angles (degrees).
    // The CSS rule is: transparent 0 from, filled from to, transparent to 360.
    bipolarFillFrom() {
      const v = this.value ?? 0;
      if (v === 0) return 360; // no fill
      if (v > 0) return 0;     // positive: fill starts at 12 o'clock
      // negative: fill ends at 360° (= 12 o'clock), starts below it
      return 360 - (Math.abs(v) / this.range) * 120;
    },

    bipolarFillTo() {
      const v = this.value ?? 0;
      if (v === 0) return 360; // no fill
      if (v < 0) return 360;   // negative: fill ends at 360° (= 12 o'clock)
      // positive: fill starts at 0° (= 12 o'clock), ends clockwise
      return (v / this.range) * 120;
    },

    // ------------------------------------------------------------------ //
    // ENUM
    // ------------------------------------------------------------------ //
    enumIndex() {
      const idx = this.options.findIndex(o => o.value === this.value);
      return idx >= 0 ? idx : 0;
    },

    enumLabel() {
      return this.options[this.enumIndex]?.label ?? '';
    },

    // Optional notation-glyph descriptor for the current enum option (Div knob,
    // MDL-290). Null for every option that has no glyph — text-only enum knobs
    // (accent, boundary, …) render exactly as before.
    enumGlyph() {
      return this.isEnum ? (this.options[this.enumIndex]?.glyph ?? null) : null;
    },

    // Hover description for the current enum option (its `tip`, falling back to
    // the label). Used to caption the glyph readout, whose visible text was
    // removed — e.g. "crotchet / quarter note".
    enumTip() {
      if (!this.isEnum) return '';
      const opt = this.options[this.enumIndex];
      return opt?.tip ?? opt?.label ?? '';
    },

    enumPct() {
      if (this.options.length <= 1) return 0;
      return this.enumIndex / (this.options.length - 1);
    },

    enumFillDeg() {
      return this.enumPct * 360;
    },

    // Circular variant: circular layout — index 0 sits at 12 o'clock (0°),
    // each step advances by 360/n degrees.
    circularPct() {
      const n = this.options.length;
      if (n === 0) return 0;
      return this.enumIndex / n;
    },

    // ------------------------------------------------------------------ //
    // Live ghost marker (MDL-269)
    // ------------------------------------------------------------------ //
    // Ring angle for the injected live value, in the same coordinate
    // system as the snap ticks (0° = 12 o'clock). Null hides the marker.
    ghostAngle() {
      const v = this.ccLiveValue;
      if (v == null) return null;
      if (this.isEnum) {
        const idx = this.options.findIndex(o => o.value === v);
        if (idx < 0) return null;
        if (this.isCircular) return (idx / this.options.length) * 360;
        return this.options.length <= 1 ? 0 : (idx / (this.options.length - 1)) * 360;
      }
      if (this.isBipolar) {
        // Same arc as the cap: -range → -120°, 0 → 0°, +range → +120°
        return (v / this.range) * 120;
      }
      let pct;
      if (this.logarithmic) {
        const clamped = Math.max(this.min, Math.min(this.max, v));
        pct = (Math.log(clamped) - Math.log(this.min)) / (Math.log(this.max) - Math.log(this.min));
      } else {
        pct = (v - this.min) / (this.max - this.min);
      }
      return Math.max(0, Math.min(1, pct)) * 360;
    },

    // ------------------------------------------------------------------ //
    // Combined knobStyle
    // ------------------------------------------------------------------ //
    knobStyle() {
      if (this.isBipolar) {
        return {
          '--knob-pct': this.bipolarPct,
          '--knob-bipolar-cap-deg': this.bipolarCapDeg + 'deg',
          '--knob-bipolar-fill-from': this.bipolarFillFrom + 'deg',
          '--knob-bipolar-fill-to': this.bipolarFillTo + 'deg'
        };
      }
      if (this.isEnum && this.isCircular) {
        return {
          '--knob-pct': this.circularPct,
          '--knob-snap-count': this.options.length
        };
      }
      if (this.isEnum) {
        return {
          '--knob-pct': this.enumPct,
          '--knob-fill': this.enumFillDeg + 'deg',
          '--knob-snap-count': this.options.length
        };
      }
      // unipolar
      return {
        '--knob-pct': this.pct,
        '--knob-fill': this.fillDeg + 'deg',
        ...this.snapVars
      };
    },

    // ------------------------------------------------------------------ //
    // Display value text
    // ------------------------------------------------------------------ //
    displayValue() {
      if (this.isEnum) return this.enumLabel;
      if (this.isBipolar) {
        const v = this.value ?? 0;
        return (v > 0 ? '+' + v : String(v)) + this.unit;
      }
      if (this.logarithmic) {
        const v = this.value ?? this.min;
        return v.toFixed(2) + ' Hz';
      }
      return this.value;
    },

    // ------------------------------------------------------------------ //
    // Root CSS class list
    // ------------------------------------------------------------------ //
    rootClasses() {
      return {
        'pedal-knob': true,
        'pedal-knob--snaps': this.snaps.length > 0,
        'pedal-knob--bipolar': this.isBipolar,
        'pedal-knob--enum': this.isEnum,
        'pedal-knob--circular': this.isCircular
      };
    }
  },

  methods: {
    showTipBriefly() {
      clearTimeout(this._tipTimer);
      this.tipVisible = true;
      this._tipTimer = setTimeout(() => { this.tipVisible = false; }, 3000);
    },

    onMousedown(event) {
      if (event.touches) this.showTipBriefly();
      if (this.isBipolar) {
        this.startBipolarKnobDrag(
          event,
          () => this.value,
          v => this.$emit('update:value', v),
          this.range,
          [0, ...this.snaps]
        );
      } else if (this.isEnum) {
        this.startEnumKnobDrag(
          event,
          () => this.value,
          v => this.$emit('update:value', v),
          this.options,
          this.isCircular  // circular wrap-around for circular variant
        );
      } else {
        this.startKnobDrag(
          event,
          () => this.value,
          v => this.$emit('update:value', v),
          this.min,
          this.max,
          this.snaps,
          this.logarithmic
        );
      }
    }
  }
}
</script>
<template>
  <div :class="rootClasses" :style="knobStyle">
    <span class="pedal-knob__label">{{ label }}</span>
    <div
      class="pedal-knob__body tooltip tooltip-top"
      :class="{ 'tooltip-open': tipVisible }"
      :data-tip="tip"
      @mousedown="onMousedown($event)"
      @touchstart="onMousedown($event)"
    >
      <div class="pedal-knob__ring">
        <!-- Unipolar snap ticks -->
        <template v-if="!isBipolar && !isEnum">
          <button
            v-for="(snap, i) in snaps"
            :key="i"
            class="pedal-knob__snap-tick"
            :style="{ '--tick-angle': ((snap - min) / (max - min)) * 360 + 'deg' }"
            :aria-label="`Set to ${snap}`"
            @click.stop="$emit('update:value', snap)"
            @touchstart.stop.prevent
          ></button>
        </template>

        <!-- Enum option ticks — circular layout for circular variant, linear for others -->
        <template v-if="isEnum">
          <button
            v-for="(opt, i) in options"
            :key="opt.value"
            class="pedal-knob__snap-tick"
            :class="{ 'pedal-knob__snap-tick--active': i === enumIndex }"
            :style="{ '--tick-angle': isCircular
              ? (i / options.length) * 360 + 'deg'
              : (options.length <= 1 ? 0 : (i / (options.length - 1)) * 360) + 'deg'
            }"
            :aria-label="`Set to ${opt.label}`"
            @click.stop="$emit('update:value', opt.value)"
            @touchstart.stop.prevent
          ></button>
        </template>

        <!-- Bipolar centre-zero tick, and any further snap ticks -->
        <template v-if="isBipolar">
          <div class="pedal-knob__bipolar-zero"></div>
          <button
            v-for="tick in bipolarSnapTicks"
            :key="tick.value"
            class="pedal-knob__snap-tick"
            :style="{ '--tick-angle': tick.angle + 'deg' }"
            :aria-label="`Set to ${tick.value}${unit}`"
            @click.stop="$emit('update:value', tick.value)"
            @touchstart.stop.prevent
          ></button>
        </template>

        <!-- Live incoming-CC ghost marker (display-only) -->
        <div
          v-if="ghostAngle !== null"
          class="pedal-knob__ghost"
          :style="{ '--tick-angle': ghostAngle + 'deg' }"
        ></div>

        <div class="pedal-knob__cap">
          <div class="pedal-knob__dot"></div>
        </div>
      </div>
    </div>
    <span
      class="pedal-knob__value"
      :class="{ 'pedal-knob__value--glyph': enumGlyph, 'tooltip tooltip-bottom': enumGlyph }"
      :data-tip="enumGlyph ? enumTip : undefined"
    >
      <!-- Glyph options render the note only (no caption) to keep the readout
           compact; the name is on the glyph's hover title and the knob tip.
           Options without a glyph (bar entries, other enum knobs) show text. -->
      <NoteGlyph
        v-if="enumGlyph"
        v-bind="enumGlyph"
        :title="enumLabel"
        class="pedal-knob__glyph"
      /><template v-else>{{ displayValue }}</template>
    </span>
  </div>
</template>

<style scoped>
/* Div knob: the readout is the notation glyph alone (no caption). Vertical
   centring and the fixed readout height come from the base .pedal-knob__value
   rule (pedal-knob.css), so text and glyph readouts are the same height and the
   knob never changes size between them. */
.pedal-knob__glyph {
  font-size: 1.3em;
}
</style>
